/**
 * Ricerca di Lead e Clienti (tabella clients della propria organizzazione) per aprire
 * una chat WhatsApp: per nome (ragione sociale, referente) o per numero.
 *
 * Rispetta le stesse restrizioni di visibilita' della ricerca globale (searchStore):
 * filtro "solo i miei" e visibilita' per pipeline/fonte del ruolo.
 */
import { supabase } from '../../supabase';
import { usePermissionsStore } from '../../stores/permissionsStore';
import { EXCLUDE_REFERENTI } from '../../utils/contactPollution';
import { buildPhoneDigitPatterns } from '../../utils/phoneSearch';
import { sanitizeFilterValue, allWordsClause } from '../../utils/nameSearch';
import { leadSourceOrCondition } from '../../utils/leadVisibility';
import { clientWhatsAppNumbers } from './newChatLogic';

export interface ClientChatCandidate {
  id: string;
  name: string;
  contactName: string | null;
  /** "Lead" o "Cliente" secondo lo stato. */
  kind: 'Lead' | 'Cliente';
  numbers: string[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function searchClientsForChat(
  organizationId: string,
  userId: string | null,
  text: string,
  limit = 8,
): Promise<ClientChatCandidate[]> {
  const safe = sanitizeFilterValue(text);
  if (!organizationId || safe.length < 2) return [];

  const parts = [
    `business_name.ilike.%${safe}%`,
    `contact_name.ilike.%${safe}%`,
    `whatsapp_number.ilike.%${safe}%`,
    ...buildPhoneDigitPatterns(text).map((p) => `phone_digits.ilike.%${p}%`),
  ];
  const perParole = allWordsClause('business_name', safe);
  if (perParole) parts.push(perParole);
  const searchCond = parts.join(',');

  const perms = usePermissionsStore.getState();
  const onlyMine = perms.hasPermission('leads', 'filter_by_user') || perms.hasPermission('clients', 'filter_by_user');
  const visibility = perms.getLeadVisibility();

  let query = supabase
    .from('clients')
    .select('id, business_name, contact_name, phone, whatsapp_number, country_code, stato_cliente')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .or(EXCLUDE_REFERENTI);
  if (visibility?.pipelineIds) {
    query = query.in('pipeline_id', visibility.pipelineIds.length > 0 ? visibility.pipelineIds : ['00000000-0000-0000-0000-000000000000']);
  }
  const visibilityCond = leadSourceOrCondition(visibility);
  const scoped = visibilityCond ? `and(or(${visibilityCond}),or(${searchCond}))` : `or(${searchCond})`;
  if (onlyMine && userId) {
    query = query.or(`and(assigned_to.eq.${userId},${scoped}),and(sales_rep_id.eq.${userId},${scoped})`);
  } else {
    query = query.or(visibilityCond ? scoped : searchCond);
  }

  const { data, error } = await query.limit(limit);
  if (error) throw error;
  const rows = data || [];

  // Lead o Cliente: stato_cliente punta a client_states (is_lead); i valori legacy sono testo.
  const stateIds = [...new Set(rows.map((r) => r.stato_cliente).filter((s): s is string => !!s && UUID_RE.test(s)))];
  const isLead = new Map<string, boolean>();
  if (stateIds.length > 0) {
    const { data: states } = await supabase
      .from('client_states')
      .select('id, is_lead')
      .eq('organization_id', organizationId)
      .in('id', stateIds);
    for (const s of states || []) isLead.set(s.id, !!s.is_lead);
  }

  return rows.map((r) => {
    const stato = r.stato_cliente as string | null;
    const lead = stato ? (isLead.get(stato) ?? stato === 'lead') : false;
    return {
      id: r.id as string,
      name: (r.business_name as string) || (r.contact_name as string) || 'Senza nome',
      contactName: (r.contact_name as string | null) || null,
      kind: lead ? 'Lead' : 'Cliente',
      numbers: clientWhatsAppNumbers(r),
    };
  });
}
